import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Sparkle, Lightning, Crown, Gift, ShieldCheck, Rocket } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/contexts/ThemeContext';
import ThemeToggle from '@/components/ThemeToggle';
import PageTransition from '@/components/PageTransition';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Pricing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState('');
  const { isDark } = useTheme();

  const plans = [
    {
      id: 'monthly',
      name: 'Mensuel',
      price: '29€',
      priceValue: 29,
      period: '/mois',
      description: 'Sans engagement',
      badge: 'Sans engagement',
      badgeColor: 'bg-blue-500',
      savings: null,
      icon: Lightning,
      features: [
        'Accès complet à toutes les fonctionnalités',
        'IA + SEO + Boost + Vidéo',
        'Génération illimitée de contenu',
        'Suivi de positions Google',
        'Support prioritaire',
      ],
      cta: 'Commencer maintenant',
      popular: false,
    },
    {
      id: 'yearly',
      name: 'Annuel',
      price: '149€',
      priceValue: 149,
      period: '/an',
      monthlyEquivalent: '12,42€/mois',
      description: 'Meilleure valeur',
      badge: '🔥 Offre recommandée',
      badgeColor: 'bg-gradient-to-r from-violet-500 to-pink-500',
      savings: 'Économisez 50%',
      savingsAmount: '199€',
      icon: Crown,
      features: [
        'Tout du plan Mensuel',
        'Économisez plus de 50%',
        'Accès prioritaire aux nouveautés',
        'Support VIP dédié',
        'Fonctionnalités beta en avant-première',
      ],
      cta: 'Économiser 50%',
      popular: true,
    },
  ];

  const handleCheckout = async (packageId) => {
    setLoading(packageId);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/checkout`,
        { package_id: packageId, origin_url: originUrl },
        { withCredentials: true }
      );
      window.location.href = response.data.url;
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Veuillez vous connecter pour continuer');
        navigate('/login');
      } else {
        toast.error('Erreur lors de la création du paiement');
      }
    } finally {
      setLoading('');
    }
  };

  const handleFreeTrial = async () => {
    setLoading('trial');
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/checkout`,
        { package_id: 'trial', origin_url: originUrl },
        { withCredentials: true }
      );
      window.location.href = response.data.url;
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Veuillez vous connecter pour continuer');
        navigate('/login');
      } else {
        // Fallback: redirect to register for trial
        navigate('/register?trial=true');
      }
    } finally {
      setLoading('');
    }
  };

  return (
    <PageTransition>
      <div className={`min-h-screen relative z-10 ${isDark ? '' : 'bg-gradient-to-b from-white to-gray-50'}`}>
        {/* Navigation */}
        <nav className={`sticky top-0 z-50 backdrop-blur-xl transition-colors duration-300 ${
          isDark 
            ? 'border-b border-white/[0.06] bg-black/20' 
            : 'border-b border-gray-200/80 bg-white/80'
        }`}>
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center shadow-lg shadow-violet-500/25">
                <Sparkle size={22} weight="fill" className="text-white" />
              </div>
              <span className={`text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                SIMBLYO
              </span>
            </Link>
            <div className="flex items-center gap-4">
              <ThemeToggle />
              <Link to="/login">
                <Button 
                  variant="ghost" 
                  className={isDark ? 'text-white/80 hover:text-white hover:bg-white/[0.06]' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}
                >
                  Connexion
                </Button>
              </Link>
            </div>
          </div>
        </nav>

        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            {/* Header */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="text-center mb-12"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
                  isDark 
                    ? 'bg-violet-500/10 border border-violet-500/20' 
                    : 'bg-violet-50 border border-violet-100'
                }`}
              >
                <Gift className="text-violet-500" size={18} weight="fill" />
                <span className={`text-sm font-medium ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>
                  Essai gratuit 3 jours inclus
                </span>
              </motion.div>
              
              <h1 className={`text-4xl md:text-5xl lg:text-7xl font-extrabold mb-6 tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Un prix simple,
                <span className="block gradient-text-hero mt-2">
                  des résultats concrets
                </span>
              </h1>
              <p className={`text-lg md:text-xl max-w-2xl mx-auto ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                Votre assistant marketing IA complet. SEO, contenu, publicités — tout en un.
              </p>
            </motion.div>

            {/* Free Trial CTA */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className={`mb-12 p-6 rounded-2xl text-center ${
                isDark 
                  ? 'bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20' 
                  : 'bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100'
              }`}
            >
              <div className="flex flex-col md:flex-row items-center justify-center gap-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'
                  }`}>
                    <Rocket className="text-emerald-500" size={24} weight="fill" />
                  </div>
                  <div className="text-left">
                    <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Essayez gratuitement pendant 3 jours
                    </p>
                    <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                      Aucune carte bancaire requise
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleFreeTrial}
                  disabled={loading === 'trial'}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-3 rounded-xl font-semibold shadow-lg shadow-emerald-500/25"
                  data-testid="free-trial-btn"
                >
                  {loading === 'trial' ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                      Chargement...
                    </span>
                  ) : (
                    <>
                      <Gift className="mr-2" size={18} />
                      Démarrer l'essai gratuit
                    </>
                  )}
                </Button>
              </div>
            </motion.div>

            {/* Pricing Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {plans.map((plan, index) => (
                <motion.div
                  key={plan.id}
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className={`relative rounded-2xl p-8 transition-all duration-300 hover:-translate-y-2 ${
                    plan.popular 
                      ? 'bg-gradient-to-b from-violet-500/10 to-pink-500/5 border-2 border-violet-500/40 shadow-[0_0_50px_-10px_rgba(139,92,246,0.5)]' 
                      : isDark
                        ? 'glass-card hover:border-white/[0.15]'
                        : 'bg-white border border-gray-200 shadow-lg hover:shadow-xl'
                  }`}
                >
                  {/* Badge */}
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className={`${plan.badgeColor} text-white px-4 py-1.5 rounded-full text-sm font-semibold flex items-center gap-1.5 shadow-lg`}>
                      {plan.popular && <Sparkle size={14} weight="fill" />}
                      {plan.badge}
                    </span>
                  </div>

                  <div className="text-center mb-8 pt-4">
                    {/* Icon */}
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
                      plan.popular
                        ? 'bg-gradient-to-br from-violet-500 to-pink-500 shadow-lg shadow-violet-500/30'
                        : isDark 
                          ? 'bg-gradient-to-br from-violet-500/20 to-pink-500/20' 
                          : 'bg-violet-100'
                    }`}>
                      <plan.icon size={32} weight={plan.popular ? 'fill' : 'duotone'} className={plan.popular ? 'text-white' : 'text-violet-500'} />
                    </div>
                    
                    {/* Plan name */}
                    <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {plan.name}
                    </h2>
                    
                    {/* Price */}
                    <div className="flex items-baseline justify-center mb-2">
                      <span className={`text-5xl md:text-6xl font-bold ${plan.popular ? 'gradient-text' : isDark ? 'text-white' : 'text-gray-900'}`}>
                        {plan.price}
                      </span>
                      <span className={`ml-2 text-lg ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                        {plan.period}
                      </span>
                    </div>
                    
                    {/* Monthly equivalent for yearly */}
                    {plan.monthlyEquivalent && (
                      <p className={`text-sm mb-3 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                        soit {plan.monthlyEquivalent}
                      </p>
                    )}
                    
                    {/* Savings badge */}
                    {plan.savings && (
                      <div className="flex items-center justify-center gap-2">
                        <span className="inline-block bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-bold px-4 py-1.5 rounded-full shadow-lg shadow-emerald-500/25">
                          🔥 {plan.savings}
                        </span>
                        {plan.savingsAmount && (
                          <span className={`text-sm line-through ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
                            {plan.savingsAmount}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                          plan.popular
                            ? 'bg-gradient-to-br from-violet-500 to-pink-500'
                            : isDark ? 'bg-violet-500/20' : 'bg-violet-100'
                        }`}>
                          <Check size={14} weight="bold" className={plan.popular ? 'text-white' : 'text-violet-500'} />
                        </div>
                        <span className={`text-sm leading-relaxed ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Button
                    className={`w-full text-base py-6 font-semibold transition-all duration-300 rounded-xl ${
                      plan.popular 
                        ? 'btn-premium shadow-lg shadow-violet-500/25' 
                        : isDark
                          ? 'bg-white/[0.08] border border-white/[0.15] hover:bg-white/[0.12] text-white'
                          : 'bg-gray-900 hover:bg-gray-800 text-white'
                    }`}
                    onClick={() => handleCheckout(plan.id)}
                    disabled={loading === plan.id}
                    data-testid={`pricing-${plan.id}-btn`}
                  >
                    {loading === plan.id ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                        Chargement...
                      </span>
                    ) : plan.cta}
                  </Button>
                </motion.div>
              ))}
            </div>

            {/* Trust signals */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-16 text-center"
            >
              <div className="flex flex-wrap items-center justify-center gap-8 mb-8">
                {[
                  { icon: ShieldCheck, text: 'Paiement sécurisé' },
                  { icon: Gift, text: 'Essai gratuit 3 jours' },
                  { icon: Check, text: 'Sans engagement' },
                  { icon: Lightning, text: 'Annulation en 1 clic' },
                ].map((item, i) => (
                  <div key={i} className={`flex items-center gap-2 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                    <item.icon size={20} weight="duotone" className="text-violet-500" />
                    <span className="text-sm font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
              
              <p className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                Garantie satisfait ou remboursé de 14 jours. Aucun risque.
              </p>
            </motion.div>
          </div>
        </section>
      </div>
    </PageTransition>
  );
}
